import { ICellRendererParams } from "ag-grid-community";
import { useRef } from "react";

import { SpinnerIcon, StarsIcon, UploadIcon } from "../Icons";
import { fileToBase64, formatLocalDate, parseDateSafe } from "../../utils";
import { toast } from "react-toastify";
import { useAIParseFileMutation } from "../../store/apiSlices/logistics";
import { AIParseFilePayload } from "../../types/DealTracker";
import { initialRowData } from "./DealsTable";

export default function AIAutofillCellRenderer(params: ICellRendererParams) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [aiParseFileUpload, { isLoading }] = useAIParseFileMutation();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const keys = Object.keys(initialRowData).filter(
      (key) => key !== "upload_to_autofill" && key !== "flow",
    );

    // convert ALL files to base64
    const filesPayload = await Promise.all(
      files.map(async (file) => ({
        filename: file.name,
        base64: await fileToBase64(file),
      })),
    );

    const payload = {
      keys,
      files: filesPayload,
    };

    await handleAutofill(payload);
  };

  const mergeAIData = (rows: Record<string, any>[]) => {
    const merged: Record<string, any> = {};

    rows.forEach((row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (merged[key]) return; // already filled
        if (value === "" || value == null) return;

        merged[key] = value;
      });
    });

    return merged;
  };

  const handleAutofill = async (payload: AIParseFilePayload) => {
    try {
      const data = await aiParseFileUpload(payload).unwrap();

      if (data.status !== "success") {
        toast.error("Something went wrong! Please try again");
        return;
      }

      toast.success(data.msg || "File AI analyze done successfully.");

      const mergedAIData = mergeAIData(data.json_data ?? []);
      if (!mergedAIData) return;

      let hasUpdates = false;
      const updatedRow = { ...params.node.data };

      Object.entries(mergedAIData).forEach(([key, value]) => {
        if (key === "upload_to_autofill") return;
        if (!params.api.getColumn(key)) return;

        const cell = updatedRow[key];
        if (!cell) return;

        if (cell.column_value !== "") return;

        updatedRow[key] = {
          ...cell,
          column_value:
            cell.column_type.data_type === "date"
              ? formatLocalDate(parseDateSafe(value))
              : (value ?? ""),
        };

        hasUpdates = true;
      });

      if (hasUpdates) {
        params.node.setData(updatedRow);
        params.context?.handleSave?.();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong! Please try again");
    }
  };

  return (
    <div className="flex items-center justify-between">
      <div className="text-gray-500">
        {isLoading ? (
          <p className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-400 to-purple-500 bg-clip-text text-transparent animate-pulse">
            <StarsIcon className="size-4 text-indigo-400 drop-shadow-md" />
            AI analyzing
          </p>
        ) : (
          <span className="hover:text-gray-700 transition-colors">
            Upload file
          </span>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        multiple
      />
      <button
        type="button"
        onClick={() => {
          fileInputRef.current?.click();
        }}
        disabled={isLoading}
        className="px-2 py-1 bg-gray-200 rounded disabled:cursor-none disabled:opacity-50"
      >
        {isLoading ? (
          <SpinnerIcon className="size-5 text-gray-200 animate-spin fill-blue-600" />
        ) : (
          <UploadIcon className="size-6" />
        )}
      </button>
    </div>
  );
}
